// frontend/src/pages/admin/ManageMembersPage.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    FaTrash, FaEdit, FaUserShield, FaUser, 
    FaCheckCircle, FaTimesCircle, FaPlus, FaSearch, 
    FaGem, FaMoneyBillWave // İkonlar eklendi
} from 'react-icons/fa';

// AVATAR LİSTESİ
const AVATAR_OPTIONS = [
    '/avatar/default-avatar.png',
    '/avatar/avatar-1.png',
    '/avatar/avatar-2.png',
    '/avatar/avatar-3.png',
    '/avatar/avatar-4.png',
    '/avatar/avatar-5.png',
    '/avatar/avatar-6.png',
    '/avatar/avatar-7.png'
];

const ManageMembersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null); 
  const [msg, setMsg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form Başlangıç Değeri (isExempt eklendi)
  const initialForm = {
      name: '', email: '', password: '', role: 'user', 
      phone: '', description: '', isActive: true, 
      avatar: '/avatar/default-avatar.png',
      isExempt: false // <--- Varsayılan
  };
  const [formData, setFormData] = useState(initialForm);
  const [showModal, setShowModal] = useState(false);

  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

  const getCleanToken = () => {
      let token = localStorage.getItem('token');
      return token ? token.replace(/"/g, '') : null;
  };

  const fetchUsers = useCallback(async () => {
    try {
        const token = getCleanToken();
        if (!token) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const { data } = await axios.get(`${API_URL}/users`, config);
        setUsers(data);
        setLoading(false);
    } catch (error) {
        setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // --- SİLME ---
  const handleDelete = async (id) => {
    if (window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) {
      const token = getCleanToken();
      if (!token) return;

      try {
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        await axios.post(`${API_URL}/users/${id}?_method=DELETE`, {}, config);
        setMsg({ type: 'success', text: 'Kullanıcı silindi.' });
        fetchUsers();
      } catch (error) {
        setMsg({ type: 'error', text: 'Silme işlemi başarısız.' });
      }
    }
  };

  // --- MODAL AÇMA ---
  const handleOpenModal = (user = null) => {
    setMsg(null);
    if (user) {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '', 
            role: user.role,
            phone: user.phone || '',
            description: user.description || '',
            isActive: user.isActive, 
            avatar: user.avatar || '/avatar/default-avatar.png',
            isExempt: user.isExempt || false // <--- Veri tabanından gelen değer
        });
    } else {
        setEditingUser(null);
        setFormData(initialForm);
    }
  };

  // --- KAYDETME ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const token = getCleanToken();
    if (!token) return;

    try {
        const config = { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
        
        if (editingUser) {
            await axios.post(`${API_URL}/users/${editingUser._id}?_method=PUT`, formData, config);
            setMsg({ type: 'success', text: 'Kullanıcı güncellendi.' });
        } else {
            await axios.post(`${API_URL}/users`, formData, config);
            setMsg({ type: 'success', text: 'Yeni kullanıcı oluşturuldu.' });
        }

        setShowModal(false);
        setEditingUser(null);
        fetchUsers();

    } catch (error) {
        const errText = error.response?.data?.message || 'İşlem başarısız.';
        setMsg({ type: 'error', text: errText });
    }
  };

  const filteredUsers = users.filter(user => 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-10 text-white animate-pulse">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
            <h1 className="text-2xl font-bold text-white">Üye Yönetimi</h1>
            <span className="text-sm text-text-secondary">Toplam: {filteredUsers.length} / {users.length} Kayıt</span>
        </div>
        
        <div className="flex gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input 
                    type="text" placeholder="Ad veya E-posta ara..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-surface border border-border rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-primary text-sm"
                />
            </div>
            <button 
                onClick={() => { handleOpenModal(null); setShowModal(true); }} 
                className="bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-bold transition-colors shadow-lg shadow-blue-900/20"
            >
                <FaPlus /> Yeni Üye
            </button>
        </div>
      </div>

      {msg && (
        <div className={`p-3 rounded-lg border ${msg.type === 'success' ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-red-500/20 border-red-500/50 text-red-400'}`}>
            {msg.text}
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-text-secondary">
            <thead className="bg-gray-800 text-white uppercase font-bold text-xs">
              <tr>
                <th className="p-4">Kullanıcı</th><th className="p-4">Rol</th><th className="p-4">Durum</th><th className="p-4">Telefon</th><th className="p-4 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.map(user => (
                <tr key={user._id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="p-4 flex items-center gap-3">
                      <div className="relative">
                          <img src={user.avatar || '/avatar/default-avatar.png'} alt={user.name} className="w-10 h-10 rounded-full object-cover border border-gray-600"/>
                          {/* MUAF İKONU (AVATAR ÜZERİNDE) */}
                          {user.isExempt && (
                              <div className="absolute -top-1 -right-1 bg-yellow-500 text-black p-1 rounded-full text-[10px] border border-black" title="Ödemeden Muaf">
                                  <FaGem />
                              </div>
                          )}
                      </div>
                      <div>
                          <p className="font-medium text-white flex items-center gap-2">
                              {user.name}
                              {user.isExempt && <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1 rounded border border-yellow-500/30">MUAF</span>}
                          </p>
                          <p className="text-xs text-text-secondary font-mono">{user.email}</p>
                      </div>
                  </td>
                  <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold flex items-center gap-1 w-fit ${user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>{user.role === 'admin' ? <FaUserShield /> : <FaUser />}{user.role.toUpperCase()}</span></td>
                  <td className="p-4">{user.isActive ? <span className="text-green-400 flex items-center gap-1 text-xs font-bold"><FaCheckCircle /> AKTİF</span> : <span className="text-red-400 flex items-center gap-1 text-xs font-bold"><FaTimesCircle /> PASİF</span>}</td>
                  <td className="p-4">{user.phone || '-'}</td>
                  <td className="p-4 flex justify-center gap-2">
                    <button onClick={() => { handleOpenModal(user); setShowModal(true); }} className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"><FaEdit /></button>
                    {user.role !== 'admin' && <button onClick={() => handleDelete(user._id)} className="p-2 bg-red-600 hover:bg-red-500 text-white rounded transition-colors"><FaTrash /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && <div className="p-8 text-center text-gray-500">Kayıt bulunamadı.</div>}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-border p-6 rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
                <h2 className="text-xl font-bold text-white mb-4">{editingUser ? 'Üye Düzenle' : 'Yeni Üye Ekle'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    
                    {/* AVATAR + DURUM SWITCHLERİ */}
                    <div className="flex items-center gap-4 border-b border-border pb-4">
                        <img src={formData.avatar || '/avatar/default-avatar.png'} className="w-16 h-16 rounded-full object-cover border-2 border-primary" alt="Preview"/>
                        <div className="flex-1 space-y-2">
                            {/* AKTİF/PASİF */}
                            <label className="flex items-center gap-2 cursor-pointer bg-gray-800 p-2 rounded hover:bg-gray-700 transition-colors select-none">
                                <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({...formData, isActive: e.target.checked})} className="w-5 h-5 accent-primary"/>
                                <span className={`text-sm font-bold ${formData.isActive ? 'text-green-400' : 'text-red-400'}`}>{formData.isActive ? 'KULLANICI AKTİF' : 'KULLANICI PASİF'}</span>
                            </label>

                            {/* MUAF/DAHİL (YENİ) */}
                            <label className="flex items-center gap-2 cursor-pointer bg-gray-800 p-2 rounded hover:bg-gray-700 transition-colors select-none">
                                <input type="checkbox" checked={formData.isExempt} onChange={e => setFormData({...formData, isExempt: e.target.checked})} className="w-5 h-5 accent-yellow-500"/>
                                <div className="flex items-center gap-2">
                                    <FaMoneyBillWave className={formData.isExempt ? "text-gray-500" : "text-green-400"} />
                                    <span className={`text-sm font-bold ${formData.isExempt ? 'text-yellow-400' : 'text-gray-400'}`}>{formData.isExempt ? 'ÖDEMEDEN MUAF (VIP)' : 'ÖDEMELERE DAHİL'}</span>
                                </div>
                            </label>
                        </div>
                    </div>
                    
                    {/* AVATAR SEÇİCİ */}
                    <div>
                        <label className="block text-xs font-bold text-text-secondary mb-2">Avatar Seç</label>
                        <div className="grid grid-cols-4 gap-2">
                            {AVATAR_OPTIONS.map((av, idx) => (
                                <img 
                                    key={idx} 
                                    src={av} 
                                    onClick={() => setFormData({...formData, avatar: av})}
                                    alt={`Avatar ${idx}`}
                                    className={`w-12 h-12 rounded-full object-cover cursor-pointer border-2 transition-all hover:scale-110 ${formData.avatar === av ? 'border-primary scale-110' : 'border-transparent hover:border-gray-500'}`}
                                />
                            ))}
                        </div>
                    </div>

                    <div><label className="block text-xs font-bold text-text-secondary mb-1">Ad Soyad</label><input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-background border border-border rounded p-2 text-white outline-none" required /></div>
                    <div><label className="block text-xs font-bold text-text-secondary mb-1">E-Posta</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full bg-background border border-border rounded p-2 text-white outline-none" required /></div>
                    
                    <div>
                        <label className="block text-xs font-bold text-text-secondary mb-1">Şifre {editingUser && <span className="text-[10px] font-normal text-gray-500">(Değişmeyecekse boş bırak)</span>}</label>
                        <input type="text" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full bg-background border border-border rounded p-2 text-white outline-none" required={!editingUser} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs font-bold text-text-secondary mb-1">Rol</label><select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full bg-background border border-border rounded p-2 text-white outline-none"><option value="user">User</option><option value="admin">Admin</option></select></div>
                        <div><label className="block text-xs font-bold text-text-secondary mb-1">Telefon</label><input type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-background border border-border rounded p-2 text-white outline-none" /></div>
                    </div>
                    <div className="flex gap-3 mt-6">
                        <button type="submit" className="flex-1 bg-primary hover:bg-blue-600 text-white py-2 rounded font-bold transition-colors">{editingUser ? 'Güncelle' : 'Oluştur'}</button>
                        <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded font-bold transition-colors">İptal</button>
                    </div>
                </form>
            </div>
        </div>
      )}
    </div>
  );
};

export default ManageMembersPage;